import { indianStates } from "./enums";
import { WarehouseModel } from "../erp/inventory/models/warehouse_model";
import { AddressModel } from "../erp/accounts/models/party_model";

/**
 * Validates if a warehouse has a valid state and pincode
 * @param warehouse - The warehouse object to validate
 * @throws Error if state is not in indianStates list or pincode is not 6 digits
 */

const normalizeState = (state?: string) => state?.trim().toLowerCase();

const normalizedIndianStates = indianStates.map((s) => s.toLowerCase());

export const validateWarehouse = (warehouse: WarehouseModel): void => {
  if (!warehouse) {
    throw new Error("Warehouse data is required");
  }

  const address = warehouse.address;

  if (!address) {
    throw new Error("Warehouse address is required");
  }

  const isIndia = normalizeState(address.country) === "india";

  // Validate state
  if (isIndia) {
    if (!address.state) {
      throw new Error("Can't add a warehouse with invalid state");
    }

    const state = normalizeState(address.state);

    if (!state) {
      throw new Error("Can't add a warehouse with invalid state");
    }

    const isValidState = normalizedIndianStates.includes(state);

    if (!isValidState) {
      throw new Error("Can't add a warehouse with invalid state");
    }

    // Validate pincode - must be exactly 6 digits
    if (!address.pincode) {
      throw new Error("Can't add a warehouse with invalid pincode");
    }

    const pincodeRegex = /^\d{6}$/;
    const isValidPincode = pincodeRegex.test(address.pincode);
    if (!isValidPincode) {
      throw new Error("Can't add a warehouse with invalid pincode");
    }
  }
};

/**
 * Validates address data specifically for warehouse operations
 * @param address - The address to validate
 * @throws Error if state is not in indianStates list or pincode is not 6 digits
 */
export const validateWarehouseAddress = (address: AddressModel): void => {
  if (!address) {
    throw new Error("Address is required");
  }

  if (normalizeState(address.country) !== "india") {
    return;
  }

  // Validate state
  if (!address.state) {
    throw new Error("Can't add a warehouse with invalid state or pincode");
  }

  const state = normalizeState(address.state);

  if (!state) {
    throw new Error("Can't add a warehouse with invalid state or pincode");
  }

  const isValidState = normalizedIndianStates.includes(state);

  if (!isValidState) {
    throw new Error("Can't add a warehouse with invalid state or pincode");
  }

  // Validate pincode - must be exactly 6 digits
  if (!address.pincode) {
    throw new Error("Can't add a warehouse with invalid state or pincode");
  }

  const pincodeRegex = /^\d{6}$/;
  const isValidPincode = pincodeRegex.test(address.pincode);
  if (!isValidPincode) {
    throw new Error("Can't add a warehouse with invalid state or pincode");
  }
};

/**
 * Checks if a state is valid
 * @param state - The state name to check
 * @returns boolean - true if state is in indianStates list
 */
export const isValidState = (state: string): boolean => {
  if (!state) return false;
  return indianStates.includes(state);
};

/**
 * Checks if a pincode is valid (must be 6 digits)
 * @param pincode - The pincode to check
 * @returns boolean - true if pincode is exactly 6 digits
 */
export const isValidPincode = (pincode: string): boolean => {
  if (!pincode) return false;
  const pincodeRegex = /^\d{6}$/;
  return pincodeRegex.test(pincode);
};
